import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, ArrowRight, CheckCircle, Brain, User, FileText, Target, Package, Sparkles, Zap, TrendingUp, Users2, Calendar, FolderOpen, Clock } from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface MethodologiePRIsMEModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MethodologiePRIsMEModal({ isOpen, onClose }: MethodologiePRIsMEModalProps) {
  const [activeTab, setActiveTab] = useState<'workflow' | 'mindset' | 'onboarding'>('mindset');
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['methodologie_prisme_last_ai_analysis'] ?? null) : null;
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl max-w-7xl w-full max-h-[90vh] border border-indigo-500/30 shadow-2xl shadow-indigo-500/20 flex flex-col">
        {/* Header */}
        <div className="relative p-8 border-b border-indigo-500/30">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                    Méthodologie PRIsME
                  </h2>
                  {/* Badge AI - Disponible dans AI Assistant */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                    className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                    title="Disponible dans AI Assistant"
                  >
                    <Brain className="w-4 h-4 text-purple-200" />
                    <span className="text-xs text-purple-100 font-semibold">AI</span>
                  </motion.div>
                </div>
                <p className="text-gray-400 mt-1 text-lg mb-2">
                  Le workflow universel: Simple. Répétable. Puissant.
                </p>
                {lastAnalysisDate && (
                  <div className="flex items-center gap-2 text-xs text-purple-300">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="flex gap-2 px-8 pt-6 border-b border-indigo-500/30">
          <button
            onClick={() => setActiveTab('mindset')}
            className={`flex items-center gap-2 px-6 py-3 font-semibold transition-all rounded-t-lg ${
              activeTab === 'mindset'
                ? 'bg-indigo-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Zap className="w-5 h-5" />
            <span>Mindset IA & Excellence</span>
          </button>
          <button
            onClick={() => setActiveTab('onboarding')}
            className={`flex items-center gap-2 px-6 py-3 font-semibold transition-all rounded-t-lg ${
              activeTab === 'onboarding'
                ? 'bg-indigo-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Users2 className="w-5 h-5" />
            <span>Onboarding Client</span>
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-10 overflow-y-auto flex-1">
          
          {/* ONGLET 2: WORKFLOW PRIsME */}
          {activeTab === 'workflow' && (
            <>
          
          {/* Message Principal */}
          <div className="bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-purple-600/20 rounded-xl p-6 border-2 border-purple-500/50">
            <h3 className="text-2xl font-bold text-center text-white mb-3">
              Le contenu change. La mécanique reste la même.
            </h3>
            <p className="text-gray-300 text-center text-lg">
              Que ce soit pour les Ventes, la TI, l'Administration ou n'importe quel département, 
              <strong className="text-purple-400"> PRIsME applique toujours le même workflow</strong> simple et efficace.
            </p>
          </div>

          {/* Le Workflow Visuel */}
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 mb-8">
              Les 6 Étapes du Workflow PRIsME
            </h3>

            {/* Étape 1: Prise de Besoin */}
            <div className="relative">
              <div className="flex items-center gap-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-blue-500/20 border-2 border-blue-500 flex items-center justify-center">
                    <span className="text-2xl font-bold text-blue-400">1</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    <FileText className="w-6 h-6 text-blue-400" />
                    Prise de Besoin Holistique
                  </h4>
                  <p className="text-gray-300">
                    Un formulaire structuré capture <strong className="text-white">TOUS les besoins</strong> du client 
                    de façon complète et organisée. Aucune information ne se perd.
                  </p>
                </div>
              </div>
              <div className="flex justify-center my-3">
                <ArrowRight className="w-8 h-8 text-indigo-400" />
              </div>
            </div>

            {/* Étape 2: IA Analyse */}
            <div className="relative">
              <div className="flex items-center gap-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-purple-500/20 border-2 border-purple-500 flex items-center justify-center">
                    <span className="text-2xl font-bold text-purple-400">2</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    <Brain className="w-6 h-6 text-purple-400" />
                    IA Analyse le Besoin
                  </h4>
                  <p className="text-gray-300">
                    L'Intelligence Artificielle <strong className="text-white">décortique, structure et analyse</strong> la prise de besoin. 
                    Elle identifie les enjeux, suggère des solutions, et prépare le terrain.
                  </p>
                </div>
              </div>
              <div className="flex justify-center my-3">
                <ArrowRight className="w-8 h-8 text-indigo-400" />
              </div>
            </div>

            {/* Étape 3: EXPERT HUMAIN - LA RICHESSE */}
            <div className="relative">
              <div className="flex items-center gap-4 bg-gradient-to-r from-yellow-600/20 via-orange-600/20 to-yellow-600/20 rounded-xl p-8 border-4 border-yellow-500/50 shadow-2xl shadow-yellow-500/30">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-yellow-500/30 border-4 border-yellow-500 flex items-center justify-center animate-pulse">
                    <span className="text-3xl font-bold text-yellow-400">3</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <User className="w-8 h-8 text-yellow-400" />
                    <h4 className="text-2xl font-bold text-white">
                      L'Expert Humain Approuve
                    </h4>
                    <div className="flex gap-1">
                      <span className="text-2xl">💎</span>
                      <span className="text-2xl">⭐</span>
                      <span className="text-2xl">🔥</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <p className="text-gray-200 text-lg font-semibold">
                      C'est ICI que la <strong className="text-yellow-400">VRAIE MAGIE</strong> opère.
                    </p>
                    <p className="text-gray-300">
                      L'Expert Humain <strong className="text-white">(VOUS)</strong> 
                      <strong className="text-white"> lit rigoureusement</strong> l'analyse de l'IA, 
                      <strong className="text-white"> valide</strong> les recommandations, 
                      <strong className="text-white"> ajuste</strong> avec votre expertise, 
                      et <strong className="text-yellow-400"> APPROUVE</strong>.
                    </p>
                    <div className="bg-yellow-500/10 rounded-lg p-4 border border-yellow-500/30 mt-4">
                      <p className="text-yellow-300 font-bold text-center text-lg">
                        🌟 L'EXPERT HUMAIN = LA RICHESSE DE PRIsME 🌟
                      </p>
                      <p className="text-gray-300 text-center mt-2">
                        C'est <strong className="text-white">VOUS</strong> qui garantissez la qualité. 
                        C'est <strong className="text-white">VOUS</strong> qui apportez la nuance. 
                        C'est <strong className="text-white">VOUS</strong> la vraie valeur.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center my-3">
                <ArrowRight className="w-8 h-8 text-indigo-400" />
              </div>
            </div>

            {/* Étape 4: Spécifications d'exigences */}
            <div className="relative">
              <div className="flex items-center gap-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
                    <span className="text-2xl font-bold text-green-400">4</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    <FileText className="w-6 h-6 text-green-400" />
                    Spécifications d'exigences se Bâtissent
                  </h4>
                  <p className="text-gray-300">
                    L'IA génère les <strong className="text-white">spécifications d'exigences</strong> (macro ou détaillées selon le contexte). 
                    L'Expert Humain <strong className="text-green-400">approuve et ajuste</strong> au besoin.
                  </p>
                </div>
              </div>
              <div className="flex justify-center my-3">
                <ArrowRight className="w-8 h-8 text-indigo-400" />
              </div>
            </div>

            {/* Étape 5: Décision du Livrable */}
            <div className="relative">
              <div className="flex items-center gap-4 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-indigo-500/20 border-2 border-indigo-500 flex items-center justify-center">
                    <span className="text-2xl font-bold text-indigo-400">5</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    <Target className="w-6 h-6 text-indigo-400" />
                    Décision du Livrable Tangible
                  </h4>
                  <p className="text-gray-300 mb-3">
                    Selon l'analyse de la prise de besoin, on <strong className="text-white">choisit</strong> le bon livrable tangible:
                  </p>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div className="bg-slate-800/50 rounded-lg p-3 border border-blue-500/30">
                      <div className="text-blue-400 font-bold text-sm mb-1">🎯 Stratégique</div>
                      <div className="text-xs text-gray-400">Plan Directeur TI</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 border border-purple-500/30">
                      <div className="text-purple-400 font-bold text-sm mb-1">📊 Tactique</div>
                      <div className="text-xs text-gray-400">Budget annuel, ODS</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 border border-pink-500/30">
                      <div className="text-pink-400 font-bold text-sm mb-1">⚙️ Opérationnel</div>
                      <div className="text-xs text-gray-400">Roadmap détaillé</div>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm mt-3">
                    <strong className="text-indigo-400">IA suggère</strong> le livrable approprié, 
                    <strong className="text-yellow-400"> l'Expert tranche</strong>.
                  </p>
                </div>
              </div>
              <div className="flex justify-center my-3">
                <ArrowRight className="w-8 h-8 text-indigo-400" />
              </div>
            </div>

            {/* Étape 6: Livrable Final */}
            <div className="relative">
              <div className="flex items-center gap-4 bg-gradient-to-r from-pink-600/20 to-rose-600/20 rounded-xl p-6 border-2 border-pink-500/50 shadow-lg shadow-pink-500/20">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-pink-500/30 border-2 border-pink-500 flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-pink-400" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    <Package className="w-6 h-6 text-pink-400" />
                    Livrable Tangible Final
                  </h4>
                  <p className="text-gray-300">
                    Le client reçoit son <strong className="text-white">livrable final</strong> selon ses attentes: 
                    un document professionnel, structuré, et aligné sur ses besoins réels.
                  </p>
                  <div className="mt-3 flex items-center gap-2 text-pink-400 font-semibold">
                    <span className="text-2xl">✨</span>
                    <span>Simple. Rapide. Efficace.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Séparateur */}
          <div className="my-8 h-0.5 bg-gradient-to-r from-transparent via-indigo-500 to-transparent"></div>

          {/* Exemples Concrets */}
          <div className="space-y-8">
            <h3 className="text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-6">
              Exemples Concrets
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Exemple 1: Ventes */}
              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <span className="text-3xl">💼</span>
                  </div>
                  <h4 className="text-2xl font-bold text-blue-400">Département Ventes</h4>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <div className="text-blue-400 font-bold min-w-[30px]">1.</div>
                    <div className="text-gray-300">
                      <strong className="text-white">Prise de besoin holistique</strong> avec le prospect
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-purple-400 font-bold min-w-[30px]">2.</div>
                    <div className="text-gray-300">
                      <strong className="text-white">IA analyse</strong> les besoins du prospect
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-yellow-400 font-bold min-w-[30px]">3.</div>
                    <div className="text-gray-300">
                      <strong className="text-yellow-400">Expert Humain (VOUS)</strong> valide l'analyse
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-green-400 font-bold min-w-[30px]">4.</div>
                    <div className="text-gray-300">
                      <strong className="text-white">Spécifications d'exigences macro</strong> se bâtissent
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-indigo-400 font-bold min-w-[30px]">5.</div>
                    <div className="text-gray-300">
                      <strong className="text-white">Décision:</strong> Générer un <strong className="text-indigo-400">ODS + Roadmap macro annuel</strong>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-pink-400 font-bold min-w-[30px]">6.</div>
                    <div className="text-gray-300">
                      <strong className="text-pink-400">Livrable:</strong> Document ODS professionnel + Roadmap visuel
                    </div>
                  </div>
                </div>

                <div className="mt-4 bg-blue-500/10 rounded-lg p-3 border border-blue-500/30">
                  <p className="text-blue-300 text-sm font-semibold">
                    ✅ Prospect impressionné • Temps: minutes • Qualité: professionnelle
                  </p>
                </div>
              </div>

              {/* Exemple 2: TI */}
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <span className="text-3xl">💻</span>
                  </div>
                  <h4 className="text-2xl font-bold text-purple-400">Département TI</h4>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <div className="text-blue-400 font-bold min-w-[30px]">1.</div>
                    <div className="text-gray-300">
                      <strong className="text-white">Prise de besoin holistique</strong> avec le client
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-purple-400 font-bold min-w-[30px]">2.</div>
                    <div className="text-gray-300">
                      <strong className="text-white">IA analyse</strong> la maturité TI actuelle
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-yellow-400 font-bold min-w-[30px]">3.</div>
                    <div className="text-gray-300">
                      <strong className="text-yellow-400">Expert Humain (VOUS)</strong> valide et ajuste
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-green-400 font-bold min-w-[30px]">4.</div>
                    <div className="text-gray-300">
                      <strong className="text-white">Spécifications d'exigences détaillées</strong> se bâtissent
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-indigo-400 font-bold min-w-[30px]">5.</div>
                    <div className="text-gray-300">
                      <strong className="text-white">Décision:</strong> Selon niveau → 
                      <strong className="text-indigo-400"> Plan Directeur</strong> OU 
                      <strong className="text-purple-400"> Budget Annuel</strong> OU 
                      <strong className="text-pink-400"> Roadmap Détaillé</strong>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-pink-400 font-bold min-w-[30px]">6.</div>
                    <div className="text-gray-300">
                      <strong className="text-pink-400">Livrable:</strong> Document stratégique/tactique/opérationnel selon besoin
                    </div>
                  </div>
                </div>

                <div className="mt-4 bg-purple-500/10 rounded-lg p-3 border border-purple-500/30">
                  <p className="text-purple-300 text-sm font-semibold">
                    ✅ Client rassuré • Livrable adapté à son niveau • Excellence garantie
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* La Puissance de la Répétabilité */}
          <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-pink-600/20 rounded-xl p-8 border-2 border-indigo-500/50">
            <h3 className="text-2xl font-bold text-center text-white mb-4">
              🔁 La Puissance de la Répétabilité
            </h3>
            <div className="space-y-3 text-gray-300 text-lg">
              <p className="text-center">
                <strong className="text-indigo-400">Le même workflow.</strong> Toujours.
              </p>
              <p className="text-center">
                <strong className="text-purple-400">Des résultats différents.</strong> À chaque fois.
              </p>
              <p className="text-center">
                <strong className="text-pink-400">Une qualité constante.</strong> Garantie.
              </p>
            </div>
            <div className="mt-6 text-center">
              <p className="text-white font-bold text-xl">
                C'est ça, PRIsME. 💎
              </p>
            </div>
          </div>
            </>
          )}

          {/* ONGLET 1: MINDSET IA & EXCELLENCE */}
          {activeTab === 'mindset' && (
            <>
              {/* Hero Message */}
              <div className="bg-gradient-to-r from-orange-600/20 via-red-600/20 to-orange-600/20 rounded-xl p-8 border-2 border-orange-500/50">
                <h3 className="text-3xl font-bold text-center text-white mb-4">
                  L'Excellence chez BZ Inc. : Notre Objectif Commun
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  Ensemble, visons l'excellence dès aujourd'hui. Voici le parcours qui transforme chaque membre de l'équipe en expert d'exception.
                </p>
              </div>

              {/* Timeline Visuelle des 4 Étapes */}
              <div className="space-y-8">
                <h3 className="text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400 mb-12">
                  Le Parcours vers l'Excellence
                </h3>

                {/* ÉTAPE 1: VISER L'EXCELLENCE */}
                <div className="relative pl-8 border-l-4 border-orange-500">
                  <div className="absolute -left-5 top-0 w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xl">
                    1
                  </div>
                  <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-xl p-6 border border-orange-500/30 ml-4">
                    <h4 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
                      <Target className="w-7 h-7 text-orange-400" />
                      Viser l'Excellence
                    </h4>
                    <p className="text-gray-300 text-lg mb-4">
                      <strong className="text-white">L'IA est disponible maintenant.</strong> Elle est là, puissante, prête à vous accompagner dans votre parcours vers l'excellence.
                    </p>
                    <p className="text-gray-300 text-lg">
                      Notre objectif commun: <strong className="text-orange-400">L'EXCELLENCE</strong>. Dans tout ce que nous faisons. Ensemble, nous y arrivons.
                    </p>
                  </div>
                </div>

                {/* ÉTAPE 2: FAIRE LE DEUIL */}
                <div className="relative pl-8 border-l-4 border-purple-500">
                  <div className="absolute -left-5 top-0 w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-xl">
                    2
                  </div>
                  <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30 ml-4">
                    <h4 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
                      <Brain className="w-7 h-7 text-purple-400" />
                      Cultiver l'Esprit d'Apprentissage
                    </h4>
                    <p className="text-gray-300 text-lg mb-4">
                      <strong className="text-white">L'humilité est une force:</strong> Reconnaître qu'il y a toujours à apprendre n'est pas une faiblesse, c'est la <strong className="text-purple-400">condition</strong> pour grandir et évoluer.
                    </p>
                    <p className="text-gray-300 text-lg">
                      L'apprentissage continu est au cœur de l'excellence. Gardons l'esprit ouvert pour accueillir de nouvelles connaissances et compétences.
                    </p>
                  </div>
                </div>

                {/* ÉTAPE 3: DEMANDER AUX EXPERTS (IA) */}
                <div className="relative pl-8 border-l-4 border-blue-500">
                  <div className="absolute -left-5 top-0 w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-xl">
                    3
                  </div>
                  <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30 ml-4">
                    <h4 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
                      <Sparkles className="w-7 h-7 text-blue-400" />
                      S'appuyer sur l'Intelligence Artificielle
                    </h4>
                    <p className="text-gray-300 text-lg mb-4">
                      <strong className="text-white">L'IA = Des millions d'experts à votre disposition.</strong> Disponibles 24/7. Prêts à vous accompagner vers l'excellence.
                    </p>
                    <p className="text-gray-300 text-lg mb-4">
                      <strong className="text-blue-400">L'approche est simple:</strong> Demandez. Découvrez. Apprenez. Répétez. Quotidiennement.
                    </p>
                    <div className="bg-blue-500/20 rounded-lg p-4 border border-blue-500/50">
                      <p className="text-white font-bold mb-2">Résultats garantis:</p>
                      <ul className="space-y-2 text-gray-200">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-blue-400" />
                          <span><strong>30 jours</strong> → Sentiment de POUVOIR</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-blue-400" />
                          <span><strong>3 mois</strong> → EXCELLENCE atteinte</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* ÉTAPE 4: AUTOMATISATION (Après Excellence) */}
                <div className="relative pl-8 border-l-4 border-green-500">
                  <div className="absolute -left-5 top-0 w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-xl">
                    4
                  </div>
                  <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30 ml-4">
                    <h4 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
                      <TrendingUp className="w-7 h-7 text-green-400" />
                      Automatisation et Optimisation
                    </h4>
                    <p className="text-gray-300 text-lg mb-4">
                      <strong className="text-white">Une fois que vous comprenez,</strong> vous pouvez automatiser. Nouvel objectif: <strong className="text-green-400">OPTIMISATION</strong>.
                    </p>
                    <p className="text-gray-300 text-lg mb-4">
                      Vous êtes maintenant au niveau d'excellence pour BZ Inc. Vous pouvez servir nos clients avec excellence.
                    </p>
                    <div className="bg-green-500/20 rounded-lg p-4 border-2 border-green-500/50">
                      <p className="text-green-300 font-bold mb-2">💡 Point Important:</p>
                      <p className="text-gray-200">
                        <strong className="text-white">Il est essentiel de comprendre chaque étape que l'IA effectue pour vous.</strong> Cette compréhension garantit la qualité et la pertinence de votre travail.
                      </p>
                      <p className="text-gray-200 mt-2">
                        C'est <strong className="text-white">vous</strong> qui présentez. C'est <strong className="text-white">vous</strong> qui avez la relation client. C'est <strong className="text-white">votre</strong> vision des besoins client qui fait la différence.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages Clés */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl p-6 border border-indigo-500/50">
                  <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                    <Users2 className="w-6 h-6 text-indigo-400" />
                    PRIsME ne diminue pas l'équipe
                  </h4>
                  <p className="text-gray-300">
                    <strong className="text-white">Au contraire!</strong> L'IA vous rend plus puissants. Nous voulons des humains d'exception avec des outils exceptionnels.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-xl p-6 border border-red-500/50">
                  <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                    <Zap className="w-6 h-6 text-red-400" />
                    L'excellence comme différenciateur
                  </h4>
                  <p className="text-gray-300">
                    <strong className="text-white">Dans un marché compétitif,</strong> l'excellence est notre différenciateur. C'est ce qui nous distingue et garantit notre succès à long terme.
                  </p>
                </div>
              </div>
            </>
          )}

          {/* ONGLET 3: ONBOARDING CLIENT */}
          {activeTab === 'onboarding' && (
            <>
              {/* Hero Message */}
              <div className="bg-gradient-to-r from-cyan-600/20 via-blue-600/20 to-cyan-600/20 rounded-xl p-8 border-2 border-cyan-500/50">
                <h3 className="text-3xl font-bold text-center text-white mb-4">
                  Onboarding Client: De la Signature à l'Excellence
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  Le processus qui transforme un <strong className="text-cyan-400">contrat signé</strong> en <strong className="text-cyan-400">partenariat d'excellence durable</strong>.
                </p>
              </div>

              {/* Timeline Onboarding */}
              <div className="space-y-6">
                <h3 className="text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 mb-8">
                  Le Processus Onboarding BZ
                </h3>

                {/* ÉTAPE 1: Setup PRIsME */}
                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-xl">
                      1
                    </div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
                        <FolderOpen className="w-6 h-6 text-purple-400" />
                        Créer l'Espace PRIsME Personnalisé
                      </h4>
                      <p className="text-gray-300 mb-4">
                        <strong className="text-white">Point de départ:</strong> Le client a signé le contrat et est transféré à l'équipe de service. Le Prime prend les rênes.
                      </p>
                      <p className="text-gray-300">
                        <strong className="text-purple-400">Action:</strong> Créer un espace PRIsME dédié au client avec son branding et ses informations.
                      </p>
                    </div>
                  </div>
                </div>

                {/* ÉTAPE 2: Créer les 3 Cards Essentielles */}
                <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-xl">
                      2
                    </div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
                        <Package className="w-6 h-6 text-blue-400" />
                        Créer les 3 Cards Fondamentales
                      </h4>
                      <div className="space-y-4">
                        <div className="bg-blue-500/20 rounded-lg p-4 border border-blue-500/50">
                          <p className="text-white font-bold mb-2">Card 1: Gouvernance du Comité TI</p>
                          <p className="text-gray-300 text-sm">Structure, rôles, responsabilités, RACI du comité de gouvernance TI.</p>
                        </div>
                        <div className="bg-blue-500/20 rounded-lg p-4 border border-blue-500/50">
                          <p className="text-white font-bold mb-2">Card 2: Réunions Comité TI</p>
                          <p className="text-gray-300 text-sm mb-2">Planifier TOUTES les réunions de l'année + Remplir l'ordre du jour des 2 premières rencontres.</p>
                        </div>
                        <div className="bg-blue-500/20 rounded-lg p-4 border border-blue-500/50">
                          <p className="text-white font-bold mb-2">Card 3: Prise de Besoin Holistique</p>
                          <p className="text-gray-300 text-sm">Formulaire complet pour capturer tous les besoins du client.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ÉTAPE 3: Première Rencontre */}
                <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-xl">
                      3
                    </div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
                        <Calendar className="w-6 h-6 text-green-400" />
                        Première Rencontre: Présentation & Prise de Besoin
                      </h4>
                      <p className="text-gray-300 mb-4">
                        <strong className="text-white">Objectifs de la rencontre:</strong>
                      </p>
                      <ul className="space-y-2 text-gray-300">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Présenter PRIsME et les 3 cards créées</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Remplir la Prise de Besoin Holistique avec le client</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Confirmer que TOUS les besoins sont compris</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Annoncer les livrables de la prochaine rencontre</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* ÉTAPE 4: Prochaine Rencontre - Livrables */}
                <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-xl p-6 border border-orange-500/30">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xl">
                      4
                    </div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
                        <FileText className="w-6 h-6 text-orange-400" />
                        Prochaine Rencontre: Les 4 Livrables Majeurs
                      </h4>
                      <p className="text-gray-300 mb-4">
                        <strong className="text-white">Tu promets au client que lors de la prochaine rencontre, tu apporteras:</strong>
                      </p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-orange-500/20 rounded-lg p-4 border border-orange-500/50">
                          <p className="text-white font-bold">📋 Plan Directeur</p>
                          <p className="text-gray-300 text-sm">Vision stratégique 3-5 ans</p>
                        </div>
                        <div className="bg-orange-500/20 rounded-lg p-4 border border-orange-500/50">
                          <p className="text-white font-bold">📊 Cadre BZ</p>
                          <p className="text-gray-300 text-sm">70 tactiques concrètes</p>
                        </div>
                        <div className="bg-orange-500/20 rounded-lg p-4 border border-orange-500/50">
                          <p className="text-white font-bold">🗓️ Roadmap de l'Année</p>
                          <p className="text-gray-300 text-sm">Plan trimestriel détaillé</p>
                        </div>
                        <div className="bg-orange-500/20 rounded-lg p-4 border border-orange-500/50">
                          <p className="text-white font-bold">📝 Spécifications d'exigences</p>
                          <p className="text-gray-300 text-sm">Spécifications complètes</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ÉTAPE 5: L'Histoire Continue */}
                <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold text-xl">
                      ∞
                    </div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-bold text-white mb-3">
                        L'Histoire s'Écrit de Rencontre en Rencontre
                      </h4>
                      <p className="text-gray-300 mb-4">
                        <strong className="text-white">À partir de là, la magie opère:</strong> Vous êtes dans le monde des GRANDS.
                      </p>
                      <div className="bg-indigo-500/20 rounded-lg p-4 border border-indigo-500/50">
                        <p className="text-white font-bold mb-2">🎯 Résultat:</p>
                        <p className="text-gray-200">
                          Tu gères les <strong className="text-indigo-400">stratégies d'affaires</strong>, l'<strong className="text-indigo-400">exécution</strong> et le <strong className="text-indigo-400">management des plans tactiques</strong>.
                        </p>
                        <p className="text-gray-200 mt-2">
                          Tu deviens le <strong className="text-white">MEILLEUR DÉPARTEMENT</strong> de l'entreprise pour qui tu travailles.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Message Final Motivant */}
              <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 rounded-xl p-8 border-2 border-cyan-400">
                <h3 className="text-2xl font-bold text-center text-white mb-3">
                  🚀 Ce Processus Change TOUT
                </h3>
                <p className="text-white text-center text-lg">
                  Tu ne livres plus seulement un service. Tu bâtis des <strong>partenariats stratégiques</strong> qui transforment des entreprises.
                </p>
                <p className="text-cyan-100 text-center mt-2">
                  C'est ça, l'Excellence BZ.
                </p>
              </div>
            </>
          )}

        </div>

        {/* Footer */}
        <div className="p-6 border-t border-indigo-500/30 bg-slate-900/50">
          <div className="flex items-center justify-between">
            <p className="text-gray-400 text-sm">
              Méthodologie PRIsME • Simple • Répétable • Puissant
            </p>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 font-semibold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:scale-105"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

